import React, { useState, useEffect, useCallback, useRef } from 'react';
import { apiClient, errorMessage } from '../../api';
import { Chip, cardClasses, inputClasses, primaryButton, smallNeutral } from '../../components/ui';
import { normalizeFiles } from '../../lib/files';
import { failureText, notReceiving, splitFailures } from '../../lib/publishReport';
import { useScreenState } from '../../lib/screenState';
import { plural } from '../../lib/text';
import ClientPicker from '../../dashboard/ClientPicker';
import ConfirmPublishDialog from '../../dashboard/ConfirmPublishDialog';
import { DiffList } from './CompareClients';

const StateLabel = ({ item, other }) => (item.state === 'ignored'
    ? <Chip>Ignorado en {other}</Chip>
    : <Chip tone="blue">Sin liberar en {other}</Chip>);

const VersionSelect = ({ id, label, value, options, onChange }) => (
    <label htmlFor={id} className="flex items-center gap-2 text-sm font-semibold text-slate-700">{label}
        <select id={id} value={value} onChange={e => onChange(e.target.value)} className={`${inputClasses} font-mono font-normal`}>
            <option value="">Elegí…</option>
            {options.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
        </select>
    </label>
);

// Un cliente en dos versiones: lo que tiene liberado en una, existe en la otra y ahí todavía no tiene. Liberarlo
// pasa por la misma confirmación que el tablero de Updates, con los prerrequisitos de la versión de destino.
export default function CompareVersions({ versions, clients, initialVersion, addToast, onChanged }) {
    const [storedClient, setClient] = useScreenState('clients.compareVersions.client', '');
    const [storedA, setA] = useScreenState('clients.compareVersions.a', initialVersion || '');
    const [storedB, setB] = useScreenState('clients.compareVersions.b', '');
    const [search, setSearch] = useScreenState('clients.compareVersions.search', '');
    const client = clients.some(c => c.id === storedClient) ? storedClient : '';
    const offered = (id) => (versions.some(v => v.id === id) ? id : '');
    const a = offered(storedA);
    const b = offered(storedB) === a ? '' : offered(storedB);

    const key = client && a && b ? `${client}|${a}|${b}` : '';
    const [loaded, setLoaded] = useState({ key: '', status: 'loading', diff: null });
    const wanted = useRef('');
    const compare = useCallback(async () => {
        wanted.current = key;
        if (!key) return;
        setLoaded(prev => (prev.key === key && prev.status === 'ready' ? prev : { key, status: 'loading', diff: null }));
        try {
            const diff = (await apiClient.get('/api/compare/versions', { params: { client, a, b } })).data;
            if (wanted.current === key) setLoaded({ key, status: 'ready', diff });
        } catch (e) { if (wanted.current === key) setLoaded({ key, status: 'error', diff: null }); }
    }, [key, client, a, b]);
    useEffect(() => { compare(); }, [compare]);
    const { status, diff } = loaded.key === key ? loaded : { status: 'loading', diff: null };

    // La confirmación necesita el listado de la versión de destino (prerrequisitos, dueños, quién ya lo tiene).
    const [confirming, setConfirming] = useState(null); // { target, filenames, files }
    useEffect(() => { setConfirming(null); }, [key]);
    const askToPublish = async (items, target) => {
        try {
            const files = normalizeFiles((await apiClient.get(`/api/files/${target}`)).data);
            setConfirming({ target, files, filenames: items.filter(item => item.state === 'publishable').map(item => item.name) });
        } catch (e) { addToast(errorMessage(e, `No se pudo leer ${target}.`), 'error'); }
    };
    const publish = async ({ versions: targetVersions, withDependencies }) => {
        const { filenames } = confirming;
        setConfirming(null);
        try {
            const res = await apiClient.post('/api/publish_to_clients', { filenames, clients: [client], versions: targetVersions, with_dependencies: withDependencies });
            const { successes = [] } = res.data;
            const { expected, failed } = splitFailures(res.data.failures);
            const parts = [
                successes.length > 0 ? `${plural(successes.length, 'publicación hecha', 'publicaciones hechas')}.` : failed.length === 0 && 'No había nada para publicar.',
                expected.length > 0 && `${notReceiving(expected.length, filenames.length)}.`,
                ...failed.map(f => `No se publicó ${f.filename}: ${failureText(f)}`),
            ].filter(Boolean);
            addToast(parts.join(' '), failed.length > 0 ? (successes.length > 0 ? 'warning' : 'error') : successes.length > 0 ? 'success' : 'info');
        } catch (e) { addToast(errorMessage(e), 'error'); }
        compare();
        onChanged();
    };

    const invert = () => { setA(b); setB(a); };
    const query = search.trim().toLowerCase();
    const absent = (count, from, to) => count > 0 && `${plural(count, `liberado en ${from} no existe`, `liberados en ${from} no existen`)} en ${to}.`;
    return (
        <div className={cardClasses}>
            <h3 className="font-semibold text-lg tracking-tight mb-4 text-slate-900">Comparar versiones{client && <span className="font-normal text-slate-400"> · {client}</span>}</h3>
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <ClientPicker single label="Cliente" clients={clients} selected={client} onChange={setClient} />
                <VersionSelect id="compare-version-a" label="Una versión" value={a} options={versions.filter(v => v.id !== b)} onChange={setA} />
                <span aria-hidden="true" className="text-lg text-slate-400">⇄</span>
                <VersionSelect id="compare-version-b" label="Otra versión" value={b} options={versions.filter(v => v.id !== a)} onChange={setB} />
                <button type="button" onClick={invert} disabled={!a && !b} title="Cambia de lado a las dos versiones" className={smallNeutral}>Invertir</button>
                {key && <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar update…" aria-label="Buscar update en la comparación" className={`${inputClasses} ml-auto w-56`} />}
            </div>
            {diff && <div className="flex flex-wrap items-center justify-end gap-2 mb-4">
                <button type="button" onClick={() => askToPublish(diff.only_a, b)} disabled={diff.publishable === 0}
                    title={`Revisa y libera en ${b}, para ${client}, lo que tiene liberado en ${a}`} className={`${primaryButton} whitespace-nowrap`}>Liberar en {b} lo de {a}…</button>
                <button type="button" onClick={() => askToPublish(diff.only_b, a)} disabled={diff.publishable_b === 0}
                    title={`Revisa y libera en ${a}, para ${client}, lo que tiene liberado en ${b}`} className={`${primaryButton} whitespace-nowrap`}>Liberar en {a} lo de {b}…</button>
            </div>}
            {!key && <p className="text-sm text-slate-500">Elegí un cliente y dos versiones.</p>}
            {key && status === 'loading' && <p role="status" className="text-sm text-slate-500">Comparando…</p>}
            {key && status === 'error' && (
                <div role="alert" className="flex items-center justify-between gap-3 p-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-700">
                    <span>No se pudo comparar.</span>
                    <button type="button" onClick={compare} className={`${smallNeutral} bg-white`}>Reintentar</button>
                </div>
            )}
            {diff && <>
                <p className="mb-4 text-sm text-slate-600">
                    {diff.common === 0 ? `${client} no tiene ningún update liberado en las dos.` : `${client} tiene ${plural(diff.common, 'update liberado', 'updates liberados')} en las dos.`}{' '}
                    {absent(diff.absent_in_b, a, b)} {absent(diff.absent_in_a, b, a)}
                </p>
                <div className="grid md:grid-cols-2 gap-6">
                    <DiffList title={`Liberados en ${a}, no en ${b}`} items={diff.only_a} emptyText={`En ${b} tiene todo lo de ${a} que existe ahí.`} search={query}
                        renderLabel={item => <StateLabel item={item} other={b} />} />
                    <DiffList title={`Liberados en ${b}, no en ${a}`} items={diff.only_b} emptyText={`En ${a} tiene todo lo de ${b} que existe ahí.`} search={query}
                        renderLabel={item => <StateLabel item={item} other={a} />} />
                </div>
            </>}
            {confirming && <ConfirmPublishDialog files={confirming.files} filenames={confirming.filenames} clients={[client]} currentVersion={confirming.target}
                versions={[{ id: confirming.target, name: confirming.target }]} onConfirm={publish} onCancel={() => setConfirming(null)} />}
        </div>
    );
}
